// TimesheetDocument.tsx
import React from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet
} from '@react-pdf/renderer';

// Interfaces
export interface ITimesheetEntry {
  date: string;
  days: string | number;
  miles: string | number;
  mileage: string | number;
  meals: string | number;
  motel: string | number;
  copies: string | number;
  phone: string | number;
  abstractFees: string | number;
  postage: string | number;
  shipping: string | number;
  other: string | number;
  total: string | number;
  description: string;
}

export interface ITimesheetTotals {
  days: string | number;
  miles: string | number;
  mileage: string | number;
  meals: string | number;
  motel: string | number;
  copies: string | number;
  phone: string | number;
  abstractFees: string | number;
  postage: string | number;
  shipping: string | number;
  other: string | number;
  total: string | number;
}

export interface ITimesheetStatement {
  project: string;
  projectNumber: string;
  entries: ITimesheetEntry[];
  totals: ITimesheetTotals;
}

export interface ITimesheetRecap {
  totals: ITimesheetTotals;
  projectNumbers: { project: string; number: string; services: string; expenses: string }[];
  hours: string | number;
}

export interface ITimesheetData {
  name: string;
  dlc: string;
  payPeriod: string;
  statements: ITimesheetStatement[];
  recap: ITimesheetRecap;
}

// Column headings array
const columnHeadings: string[] = [
  'Date',
  'Days',
  'Miles',
  'Mileage',
  'Meals',
  'Motel',
  'Copies',
  'Phone',
  'Abstract Fees',
  'Postage',
  'Shipping Exp.',
  'Other',
  'Total',
  'Work Description'
];

// Styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 10, // increased by 2
    fontFamily: 'Helvetica'
  },
  header: {
    marginBottom: 6,
    textAlign: 'center'
  },
  title: {
    fontSize: 12, // keep same
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 4
  },
  infoBox: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#f0f0f0',
    color: '#000000',
    padding: 6,
    marginBottom: 8
  },
  infoLeft: {
    flexDirection: 'column',
    flex: 1
  },
  infoCenter: {
    flex: 1,
    alignItems: 'center'
  },
  infoRight: {
    flex: 1,
    alignItems: 'flex-end'
  },
  label: {
    fontWeight: 'bold'
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#000000',
    color: '#FFFFFF',
    fontWeight: 'bold',
    paddingVertical: 3,
    borderBottom: '1pt solid black'
  },
  headerCell: {
    flex: 1,
    textAlign: 'center',
    paddingHorizontal: 2
  },
  row: {
    flexDirection: 'row',
    borderBottom: '0.5pt solid #ccc',
    paddingVertical: 2
  },
  cell: {
    flex: 1,
    textAlign: 'center',
    paddingHorizontal: 2
  },
  descriptionCell: {
    flex: 6,
    textAlign: 'left',
    paddingHorizontal: 2
  },
  totalsRow: {
    flexDirection: 'row',
    backgroundColor: '#3CB371',
    fontWeight: 'bold',
    paddingVertical: 3,
    marginTop: 4
  },
  totalsCell: {
    flex: 1,
    textAlign: 'center',
    paddingHorizontal: 2,
    color: '#000000', // black text
    fontWeight: 'bold'
  },
  totalsLabel: {
    flex: 1,
    textAlign: 'center',
    paddingHorizontal: 2,
    color: '#000000', // black text
    fontWeight: 'bold'
  },
  totalsDescriptionPlaceholder: {
    flex: 6 // same width as description
  }
});

// Component
const TimesheetDocument: React.FC<{ data: ITimesheetData }> = ({ data }) => (
  <Document>
    {/* Statement Pages */}
    {data.statements.map((statement, idx) => (
      <Page key={idx} size={`A4`} orientation={`landscape`} style={styles.page}>
        {/* Title */}
        <View style={styles.header}>
          <Text style={styles.title}>TIME AND EXPENSE STATEMENT</Text>
        </View>

        {/* Info Box */}
        <View style={styles.infoBox}>
          <View style={styles.infoLeft}>
            <Text>
              <Text style={styles.label}>Name: </Text>
              {data.name}
            </Text>
            <Text>Skye Gaddis Land and Mineral</Text>
          </View>

          <View style={styles.infoCenter}>
            <Text>
              <Text style={styles.label}>DLC#: </Text>
              {data.dlc}
            </Text>
            <Text>
              <Text style={styles.label}>Pay Period: </Text>
              {data.payPeriod}
            </Text>
          </View>

          <View style={styles.infoRight}>
            <Text>
              <Text style={styles.label}>Project: </Text>
              {statement.project}
            </Text>
            <Text>
              <Text style={styles.label}>Pr No.: </Text>
              {statement.projectNumber}
            </Text>
          </View>
        </View>

        {/* Table Header */}
        <View style={styles.tableHeader}>
          {columnHeadings.map((heading, i) => (
            <Text
              key={i}
              style={heading === 'Work Description' ? styles.descriptionCell : styles.headerCell}
            >
              {heading}
            </Text>
          ))}
        </View>

        {/* Data Rows */}
        {statement.entries.map((entry, i) => (
          <View style={styles.row} key={i}>
            <Text style={styles.cell}>{entry.date}</Text>
            <Text style={styles.cell}>{entry.days}</Text>
            <Text style={styles.cell}>{entry.miles}</Text>
            <Text style={styles.cell}>{entry.mileage}</Text>
            <Text style={styles.cell}>{entry.meals}</Text>
            <Text style={styles.cell}>{entry.motel}</Text>
            <Text style={styles.cell}>{entry.copies}</Text>
            <Text style={styles.cell}>{entry.phone}</Text>
            <Text style={styles.cell}>{entry.abstractFees}</Text>
            <Text style={styles.cell}>{entry.postage}</Text>
            <Text style={styles.cell}>{entry.shipping}</Text>
            <Text style={styles.cell}>{entry.other}</Text>
            <Text style={styles.cell}>{entry.total}</Text>
            <Text style={styles.descriptionCell}>{entry.description}</Text>
          </View>
        ))}

        {/* Totals Row */}
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>TOTALS</Text>
          <Text style={styles.totalsCell}>{statement.totals.days}</Text>
          <Text style={styles.totalsCell}>{statement.totals.miles}</Text>
          <Text style={styles.totalsCell}>{statement.totals.mileage}</Text>
          <Text style={styles.totalsCell}>{statement.totals.meals}</Text>
          <Text style={styles.totalsCell}>{statement.totals.motel}</Text>
          <Text style={styles.totalsCell}>{statement.totals.copies}</Text>
          <Text style={styles.totalsCell}>{statement.totals.phone}</Text>
          <Text style={styles.totalsCell}>{statement.totals.abstractFees}</Text>
          <Text style={styles.totalsCell}>{statement.totals.postage}</Text>
          <Text style={styles.totalsCell}>{statement.totals.shipping}</Text>
          <Text style={styles.totalsCell}>{statement.totals.other}</Text>
          <Text style={styles.totalsCell}>{statement.totals.total}</Text>
          <View style={styles.totalsDescriptionPlaceholder} />
        </View>
      </Page>
    ))}

    {/* Recap Page */}
    <Page size={`A4`} orientation={`landscape`} style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>TIME AND EXPENSE RECAP</Text>
      </View>

      <View style={styles.infoBox}>
        <View style={styles.infoLeft}>
          <Text>
            <Text style={styles.label}>Name: </Text>
            {data.name}
          </Text>
          <Text>Skye Gaddis Land and Mineral</Text>
        </View>

        <View style={styles.infoCenter}>
          <Text>
            <Text style={styles.label}>DLC#: </Text>
            {data.dlc}
          </Text>
          <Text>
            <Text style={styles.label}>Pay Period: </Text>
            {data.payPeriod}
          </Text>
        </View>
      </View>

      <View style={styles.tableHeader}>
        <Text style={styles.headerCell}>Project</Text>
        <Text style={styles.headerCell}>Project No.</Text>
        <Text style={styles.headerCell}>Services</Text>
        <Text style={styles.headerCell}>Expenses</Text>
      </View>

      {data.recap.projectNumbers.map((p, i) => (
        <View style={styles.row} key={i}>
          <Text style={styles.cell}>{p.project}</Text>
          <Text style={styles.cell}>{p.number}</Text>
          <Text style={styles.cell}>{p.services}</Text>
          <Text style={styles.cell}>{p.expenses}</Text>
        </View>
      ))}

      <View style={styles.totalsRow}>
        <Text style={styles.totalsLabel}>TOTALS</Text>
        <Text style={styles.totalsCell}>Days: {data.recap.totals.days}</Text>
        <Text style={styles.totalsCell}>Expenses: {data.recap.totals.total}</Text>
        <Text style={styles.totalsCell}>Hours: {data.recap.hours}</Text>
      </View>
    </Page>
  </Document>
);

export default TimesheetDocument;


// WEBPACK FOOTER //
// ./src/components/project-billing/components/TimeAndExpenseTimesheet.tsx